// Hotel-level branding (logo + wallpaper) for the Guest Assistance Platform.
// Reuses the EXISTING infrastructure end-to-end: org voucher_settings storage,
// POST /voucher-settings/logo (object storage), POST /upload/logo (Mongo-persisted
// uploads, same endpoint Review Shield/Survey wallpapers use), and the existing
// brand cascade consumed by /assistant/{card}/info. No new media system.
import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Loader2, Save, Upload, Trash2 } from 'lucide-react';
import { LiveBrandingPreview } from './LiveBrandingPreview';
import { CARD_STYLES } from './cardSurface';

const Stepper = ({ value, min, max, step, onChange, unit, minusId, plusId, sliderId }) => (
    <div className="flex items-center gap-2">
        <button type="button" onClick={() => onChange(Math.max(min, value - step))}
            className="w-7 h-7 rounded-md border text-sm font-bold hover:bg-muted shrink-0"
            data-testid={minusId}>−</button>
        <input type="range" min={min} max={max} step={step} value={value}
            onChange={(e) => onChange(parseInt(e.target.value))}
            className="w-full accent-emerald-500" data-testid={sliderId} />
        <button type="button" onClick={() => onChange(Math.min(max, value + step))}
            className="w-7 h-7 rounded-md border text-sm font-bold hover:bg-muted shrink-0"
            data-testid={plusId}>+</button>
        <span className="text-xs text-muted-foreground w-12 text-right shrink-0">{value}{unit}</span>
    </div>
);

export const HotelBrandingCard = ({ organizationId, isHotel = true }) => {
    const { t } = useTranslation();
    const [settings, setSettings] = useState(null);
    const [logoSize, setLogoSize] = useState(48);
    const [zoom, setZoom] = useState(100);
    const [busy, setBusy] = useState('');
    const [bust, setBust] = useState(0);
    const [style, setStyle] = useState('frosted');
    const [pendingWall, setPendingWall] = useState('');
    const logoRef = useRef(null);
    const wallRef = useRef(null);

    const load = useCallback(() => {
        if (!organizationId) return;
        api.get(`/voucher-settings?organization_id=${organizationId}`).then((r) => {
            const s = r.data || {};
            setSettings(s);
            setLogoSize(s.assistant_logo_size || 48);
            setZoom(s.background_image_scale || 100);
            setStyle(s.card_style || 'frosted');
            setBust(Date.now());
        }).catch(() => setSettings({}));
    }, [organizationId]);
    useEffect(() => { load(); }, [load]);

    if (!organizationId || !settings) return null;

    const core = {
        preset_amounts: settings.preset_amounts || [20, 25, 50, 75],
        preset_expiry_days: settings.preset_expiry_days || [30, 60, 90],
        default_expiry_days: settings.default_expiry_days || 30,
        default_message: settings.default_message ?? null,
        logo_size: settings.logo_size || 40,
        card_style: settings.card_style ?? null,
    };

    const put = async (extra, okMsg) => {
        await api.put(`/voucher-settings?organization_id=${organizationId}`, { ...core, ...extra });
        toast.success(okMsg);
        load();
    };

    const uploadLogo = async (file) => {
        if (!file) return;
        setBusy('logo');
        try {
            const fd = new FormData();
            fd.append('logo', file);
            fd.append('organization_id', organizationId);
            await api.post('/voucher-settings/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success(t('assistant.brandLogoSaved', 'Hotel logo uploaded'));
            load();
        } catch { toast.error(t('assistant.brandUploadFailed', 'Upload failed — use an image under 5MB')); }
        finally { setBusy(''); if (logoRef.current) logoRef.current.value = ''; }
    };

    const uploadWallpaper = async (file) => {
        if (!file) return;
        setBusy('wall');
        // Instant local preview while the upload runs — no Save needed to see it.
        const localUrl = URL.createObjectURL(file);
        setPendingWall((prev) => { if (prev) URL.revokeObjectURL(prev); return localUrl; });
        try {
            const fd = new FormData();
            fd.append('file', file);
            const r = await api.post('/upload/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            await put({ background_image_url: r.data.url, background_image_scale: zoom },
                t('assistant.brandWallSaved', 'Hotel wallpaper uploaded'));
        } catch {
            toast.error(t('assistant.brandUploadFailed', 'Upload failed — use an image under 5MB'));
            setPendingWall('');
        }
        finally { setBusy(''); if (wallRef.current) wallRef.current.value = ''; }
    };

    const hasLogo = !!settings.logo_url;
    const hasWall = !!settings.background_image_url;

    return (
        <div className="space-y-4" data-testid="hotel-branding-card">
            <p className="text-xs text-muted-foreground">
                {isHotel
                    ? t('assistant.brandHint', 'One logo and wallpaper for this hotel — every Guest Assistance Platform card of this hotel inherits them automatically. Other hotels are fully independent.')
                    : t('assistant.brandHintShared', 'One logo and wallpaper for this property — every Resident Assistance card of this property inherits them automatically. Other properties are fully independent.')}
            </p>

            {/* PROPERTY / HOTEL LOGO */}
            <div className="border rounded-lg p-3 space-y-2" data-testid="brand-logo-block">
                <Label className="text-xs font-semibold">{isHotel ? t('assistant.brandLogo', 'Hotel Logo') : t('assistant.brandLogoShared', 'Property Logo')}</Label>
                {hasLogo && (
                    <div className="bg-muted/40 border rounded-lg p-3 flex justify-center">
                        <img src={`${settings.logo_url}${settings.logo_url.includes('?') ? '&' : '?'}b=${bust}`}
                            alt="" style={{ height: `${logoSize}px` }}
                            className="object-contain max-w-[220px]" data-testid="brand-logo-preview" />
                    </div>
                )}
                <input ref={logoRef} type="file" accept="image/*" className="hidden"
                    onChange={(e) => uploadLogo(e.target.files?.[0])} />
                <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" size="sm" onClick={() => logoRef.current?.click()}
                        disabled={busy === 'logo'} data-testid="brand-logo-upload-btn">
                        {busy === 'logo' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
                        {hasLogo ? t('assistant.brandReplace', 'Replace') : t('assistant.brandUpload', 'Upload image')}
                    </Button>
                    {hasLogo && (
                        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive"
                            onClick={() => put({ remove_logo: true }, t('assistant.brandLogoRemoved', 'Hotel logo removed'))}
                            data-testid="brand-logo-remove-btn">
                            <Trash2 className="w-3 h-3 mr-1" />{t('assistant.brandRemove', 'Remove')}
                        </Button>
                    )}
                </div>
                {hasLogo && (
                    <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">{t('assistant.brandLogoSize', 'Displayed logo size (guest screen)')}</Label>
                        <Stepper value={logoSize} min={20} max={240} step={4} onChange={setLogoSize} unit="px"
                            minusId="brand-logo-size-minus" plusId="brand-logo-size-plus" sliderId="brand-logo-size-slider" />
                    </div>
                )}
            </div>

            {/* PROPERTY / HOTEL WALLPAPER */}
            <div className="border rounded-lg p-3 space-y-2" data-testid="brand-wallpaper-block">
                <Label className="text-xs font-semibold">{isHotel ? t('assistant.brandWallpaper', 'Hotel Wallpaper / Background') : t('assistant.brandWallpaperShared', 'Property Wallpaper / Background')}</Label>
                {hasWall && (
                    <div className="w-full h-28 rounded-lg border overflow-hidden relative" data-testid="brand-wallpaper-preview">
                        <div className="absolute inset-0" style={{
                            backgroundImage: `url("${settings.background_image_url}")`,
                            backgroundSize: 'cover', backgroundPosition: 'center',
                            transform: `scale(${zoom / 100})`, transformOrigin: 'center',
                        }} />
                    </div>
                )}
                <input ref={wallRef} type="file" accept="image/*" className="hidden"
                    onChange={(e) => uploadWallpaper(e.target.files?.[0])} />
                <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" size="sm" onClick={() => wallRef.current?.click()}
                        disabled={busy === 'wall'} data-testid="brand-wallpaper-upload-btn">
                        {busy === 'wall' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
                        {hasWall ? t('assistant.brandReplace', 'Replace') : t('assistant.brandUpload', 'Upload image')}
                    </Button>
                    {hasWall && (
                        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive"
                            onClick={() => { setPendingWall(''); put({ background_image_url: '' }, t('assistant.brandWallRemoved', 'Hotel wallpaper removed')); }}
                            data-testid="brand-wallpaper-remove-btn">
                            <Trash2 className="w-3 h-3 mr-1" />{t('assistant.brandRemove', 'Remove')}
                        </Button>
                    )}
                </div>
                {hasWall && (
                    <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">{t('assistant.brandZoom', 'Wallpaper zoom')}</Label>
                        <Stepper value={zoom} min={100} max={160} step={10} onChange={setZoom} unit="%"
                            minusId="brand-zoom-minus" plusId="brand-zoom-plus" sliderId="brand-zoom-slider" />
                    </div>
                )}
            </div>

            {/* CARD SURFACE STYLE + LIVE PREVIEW */}
            <div className="border rounded-lg p-3 space-y-2" data-testid="brand-surface-block">
                <Label className="text-xs font-semibold">{t('assistant.brandSurface', 'Card Surface Style')}</Label>
                <p className="text-[11px] text-muted-foreground">
                    {t('assistant.brandSurfaceHint', 'Default surface for this hotel\u2019s guest cards. The preview below shows your actual wallpaper with the selected surface \u2014 changes are instant, nothing is saved until you click Save Branding.')}
                </p>
                <div className="grid grid-cols-3 gap-2">
                    {[['frosted', t('cardStyle.frosted', 'Frosted'), t('cardStyle.frostedDesc', 'Translucent + blur')],
                      ['clear', t('cardStyle.clear', 'Clear'), t('cardStyle.clearDesc', 'Wallpaper shows through')],
                      ['solid', t('cardStyle.solid', 'Solid'), t('cardStyle.solidDesc', 'Fully opaque card')]].map(([v, label, desc]) => (
                        <button key={v} type="button" onClick={() => setStyle(v)}
                            className={`rounded-lg border-2 p-2 text-left transition-colors ${style === v
                                ? 'border-emerald-600 bg-emerald-500/10' : 'border-input hover:bg-muted/50'}`}
                            aria-pressed={style === v} data-testid={`brand-style-${v}`}>
                            <div className="text-xs font-semibold">{label}</div>
                            <div className="text-[10px] text-muted-foreground leading-tight">{desc}</div>
                        </button>
                    ))}
                </div>
                <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">{t('assistant.brandPreview', 'Preview \u2014 what your guest will see')}</Label>
                    <LiveBrandingPreview
                        wallpaperUrl={pendingWall || settings.background_image_url || ''}
                        zoom={zoom}
                        styleKey={CARD_STYLES.includes(style) ? style : 'frosted'}
                        label={style.charAt(0).toUpperCase() + style.slice(1)}
                        testId="brand-live-preview" />
                    {!(pendingWall || settings.background_image_url) && (
                        <p className="text-[11px] text-muted-foreground">
                            {t('assistant.brandPreviewNoWall', 'No wallpaper yet \u2014 the default dark background is shown.')}
                        </p>
                    )}
                </div>
            </div>

            <Button size="sm" disabled={busy === 'save'}
                onClick={async () => {
                    setBusy('save');
                    try {
                        await put({ assistant_logo_size: logoSize, background_image_scale: zoom, card_style: style },
                            t('assistant.brandSaved', 'Hotel branding saved'));
                    } catch { toast.error(t('assistant.saveFailed', 'Could not save settings')); }
                    finally { setBusy(''); }
                }}
                data-testid="brand-save-btn">
                {busy === 'save' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                {t('assistant.brandSave', 'Save Branding')}
            </Button>
        </div>
    );
};

export default HotelBrandingCard;
